// src/components/settings/AutoResponseSettings.js
import React, { useState, useEffect } from 'react';
import { Save, MessageSquare, Mail, MessageCircle, Camera, CheckCircle, AlertCircle, Info } from 'lucide-react';

const AutoResponseSettings = ({ api }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Real, production-ready default messages
  const defaultMessages = {
    email: `Thank you for contacting our support team. We have received your request and created ticket #{ticketId} for you.

Here's what happens next:
- Our support team will review your request within 24 hours
- You'll receive updates on the progress via email
- You can reply to this email to add more information

We appreciate your patience and look forward to resolving your issue promptly.

Best regards,
Support Team`,
    facebook: `Hi {customerName}! 👋

Thanks for reaching out to us. We've received your message and created ticket #{ticketId} for tracking.

Our support team will get back to you as soon as possible. We typically respond within a few hours during business hours.`,
    whatsapp: `Thanks for your message! We've received it and our team will respond as soon as possible. 🙏`,
    instagram: `Hey {customerName}! ✨

Thanks for your message. We've got it and created ticket #{ticketId} for you.

Our team will reply soon!`,
    widget: `Thank you for contacting us! We have received your message and created ticket #{ticketId}.

We've sent a confirmation to your email. Our team will review your question and respond to you via email within 2-4 hours during business hours.

Best regards,
Support Team`
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.getAutoResponseSettings();
      if (response.success) {
        // If settings exist but message is empty, use default
        const loadedSettings = { ...response.settings };
        Object.keys(loadedSettings).forEach(platform => {
          if (!loadedSettings[platform].message || loadedSettings[platform].message.trim() === '') {
            loadedSettings[platform].message = defaultMessages[platform] || '';
          }
        });
        setSettings(loadedSettings);
      }
    } catch (error) {
      console.error('Error loading auto-response settings:', error);
      // Initialize with defaults
      setSettings({
        email: { enabled: true, message: defaultMessages.email },
        facebook: { enabled: true, message: defaultMessages.facebook },
        whatsapp: { enabled: true, message: defaultMessages.whatsapp },
        instagram: { enabled: false, message: defaultMessages.instagram },
        widget: { enabled: true, message: defaultMessages.widget }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (platform) => {
    setSettings({
      ...settings,
      [platform]: {
        ...settings[platform],
        enabled: !settings[platform].enabled
      }
    });
  };

  const handleMessageChange = (platform, message) => {
    setSettings({
      ...settings,
      [platform]: {
        ...settings[platform],
        message: message
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await api.updateAutoResponseSettings(settings);
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const platforms = [
    {
      id: 'email',
      name: 'Email (Gmail)',
      icon: Mail,
      color: 'bg-red-500',
      available: true
    },
    {
      id: 'facebook',
      name: 'Facebook Messenger',
      icon: MessageSquare,
      color: 'bg-blue-500',
      available: true
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      icon: MessageCircle,
      color: 'bg-green-500',
      available: true
    },
    {
      id: 'instagram',
      name: 'Instagram Direct',
      icon: Camera,
      color: 'bg-pink-500',
      available: false
    },
    {
      id: 'widget',
      name: 'Chat Widget',
      icon: MessageSquare,
      color: 'bg-purple-500',
      available: true
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Auto-Response Settings</h2>
        <p className="text-sm text-gray-600">
          Automatically send acknowledgment messages when customers create new tickets through different channels.
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="space-y-1 text-blue-800">
              <li>• Auto-responses are sent only when a <strong>new ticket is created</strong></li>
              <li>• Replies to existing open tickets do not trigger auto-responses</li>
              <li>• Each channel can be enabled or disabled independently</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-3 rounded-lg flex items-center space-x-2 ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {saveMessage.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-600" />
          )}
          <span className="text-sm font-medium">{saveMessage.text}</span>
        </div>
      )}

      {/* Platforms */}
      <div className="space-y-4">
        {platforms.map((platform) => {
          const Icon = platform.icon;
          const platformSettings = settings[platform.id] || { enabled: false, message: '' };
          
          return (
            <div 
              key={platform.id}
              className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${
                !platform.available ? 'opacity-50' : ''
              }`}
            >
              {/* Platform Header - FIXED ALIGNMENT */}
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 ${platform.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <h3 className="font-medium text-gray-900 mb-0.5">{platform.name}</h3>
                      {platform.available ? (
                        <p className="text-sm text-gray-600 leading-tight">
                          {platformSettings.enabled 
                            ? 'Automatic responses enabled for new tickets' 
                            : 'Automatic responses are disabled'}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">Coming soon</p>
                      )}
                    </div>
                  </div>
                  
                  {platform.available && (
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={platformSettings.enabled}
                        onChange={() => handleToggle(platform.id)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  )}
                </div>
              </div>

              {/* Message Editor */}
              {platform.available && platformSettings.enabled && (
                <div className="p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message Template
                    </label>
                    <textarea
                      value={platformSettings.message}
                      onChange={(e) => handleMessageChange(platform.id, e.target.value)}
                      rows="6"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                      placeholder={defaultMessages[platform.id]}
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Available variables:</span>
                    {' '}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{customerName}'}</code>
                    {', '}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{ticketId}'}</code>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AutoResponseSettings;